// One training file as a waveform: what's kept (purple), silence that was cut
// automatically (grey), and parts the person cut (striped). Drag across it to
// pick a part, then play it or cut it; tap a part you cut to keep it again;
// tap anywhere else to hear it from there. See lib/train/clips.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play, RotateCcw, Scissors, Undo2 } from "lucide-react";
import { useT } from "@jammable/ui-core/src/i18n";
import { takeOverPlayback } from "./Sample";
import {
  type Clip,
  type Range,
  clock,
  keepOf,
  kept,
  merge,
  secsOf,
} from "@/lib/train/clips";

// A drag shorter than this (pixels) is a tap.
const TAP_PX = 6;

const STRIPES =
  "repeating-linear-gradient(135deg, rgba(46,15,82,0.16) 0 6px, rgba(46,15,82,0.04) 6px 12px)";

function usePlayback(file: Blob) {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  const audio = useRef<HTMLAudioElement | null>(null);
  const raf = useRef(0);
  const [at, setAt] = useState<number | null>(null);
  const stop = () => {
    cancelAnimationFrame(raf.current);
    audio.current?.pause();
    setAt(null);
  };
  useEffect(
    () => () => {
      cancelAnimationFrame(raf.current);
      audio.current?.pause();
      URL.revokeObjectURL(url);
    },
    [url],
  );
  const play = async (from: number, until: number) => {
    stop();
    const a = (audio.current ??= new Audio());
    if (a.src !== url) a.src = url;
    a.currentTime = from;
    // Stops the sample's own play button if it's going (Sample). Before
    // playing: the stop it hands over is this render's, so calling an older
    // one of ours afterwards would pause what just started.
    takeOverPlayback(stop);
    try {
      await a.play();
    } catch {
      return;
    }
    const tick = () => {
      if (a.paused || a.currentTime >= until) return stop();
      setAt(a.currentTime);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };
  return { at, playing: at !== null, play, stop };
}

export default function ClipEditor({
  file,
  clip,
  onCuts,
}: {
  file: Blob;
  clip: Clip;
  onCuts: (cuts: Range[]) => void;
}) {
  const t = useT();
  const analysis = clip.analysis!;
  const secs = analysis.duration;
  const bar = useRef<HTMLDivElement>(null);
  const player = usePlayback(file);
  // A picked part: [from, to], and which of their cuts it is, if one.
  const [sel, setSel] = useState<{
    a: number;
    b: number;
    cut: number | null;
    dragging: boolean;
    x0?: number;
  } | null>(null);

  const keep = useMemo(() => keepOf(clip, secs), [clip, secs]);
  const silentSecs = secsOf(merge(analysis.silent));
  const keptSecs = secsOf(keep);
  // Sound their cuts took out (silence inside a cut was going anyway).
  const cutSecs = secsOf(kept(secs, analysis.silent)) - keptSecs;
  const pct = (t: number) =>
    `${(Math.min(secs, Math.max(0, t)) / secs) * 100}%`;
  const secsAt = (x: number) => {
    const r = bar.current!.getBoundingClientRect();
    return Math.min(secs, Math.max(0, ((x - r.left) / r.width) * secs));
  };
  const isKept = (t: number) => keep.some(([s, e]) => t >= s && t < e);
  const inRange = (list: Range[], t: number) =>
    list.findIndex(([s, e]) => t >= s && t < e);

  const from = sel ? Math.min(sel.a, sel.b) : 0;
  const to = sel ? Math.max(sel.a, sel.b) : 0;
  const tickEvery = secs > 1200 ? 300 : secs > 300 ? 60 : secs > 90 ? 30 : 10;

  const tap = (t: number) => {
    const cut = inRange(clip.cuts, t);
    if (cut >= 0) {
      const [s, e] = clip.cuts[cut];
      setSel({ a: s, b: e, cut, dragging: false });
      return;
    }
    setSel(null);
    // Play to the end of the part it's in (or 8 seconds of silence).
    const part = keep.find(([s, e]) => t >= s && t < e);
    player.play(t, part ? part[1] : Math.min(secs, t + 8));
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-pt-1">
      <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-x-3 tw-gap-y-1.5">
        <span className="jam-display tw-text-[24px] tw-font-extrabold tw-leading-none tw-tabular-nums">
          {clock(keptSecs)}
          <span className="tw-text-[15px] tw-font-bold tw-text-jam-muted">
            {" "}
            {t("train.clip.keptOf", { total: clock(secs) })}
          </span>
        </span>
        <span className="tw-flex tw-flex-wrap tw-gap-1.5">
          {silentSecs >= 1 && (
            <span className="tw-rounded-full tw-bg-jam-lav tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold tw-text-jam-muted">
              {t("train.clip.silenceCut", { time: clock(silentSecs) })}
            </span>
          )}
          {cutSecs >= 1 && (
            <span className="tw-rounded-full tw-bg-jam-ink tw-px-3 tw-py-1 tw-text-xs tw-font-extrabold tw-text-white">
              {t("train.clip.cutByYou", { time: clock(cutSecs) })}
            </span>
          )}
        </span>
      </div>

      <div
        ref={bar}
        className="tw-relative tw-h-28 tw-cursor-crosshair tw-touch-none tw-select-none tw-overflow-hidden tw-rounded-[26px] tw-bg-jam-lav"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          const t = secsAt(e.clientX);
          setSel({ a: t, b: t, cut: null, dragging: true, x0: e.clientX });
        }}
        onPointerMove={(e) => {
          if (!sel?.dragging) return;
          setSel({ ...sel, b: secsAt(e.clientX) });
        }}
        onPointerUp={(e) => {
          if (!sel?.dragging) return;
          const t = secsAt(e.clientX);
          if (Math.abs(e.clientX - (sel.x0 ?? e.clientX)) < TAP_PX) tap(sel.a);
          else setSel({ ...sel, b: t, dragging: false });
        }}
        onPointerCancel={() => setSel(null)}
      >
        {clip.cuts.map(([s, e], i) => (
          <span
            key={`c${i}`}
            className="tw-absolute tw-inset-y-2 tw-rounded-[14px]"
            style={{ left: pct(s), width: pct(e - s), background: STRIPES }}
          />
        ))}
        <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-gap-[2px] tw-px-3">
          {analysis.peaks.map((p, i) => {
            const t = ((i + 0.5) / analysis.peaks.length) * secs;
            return (
              <i
                key={i}
                className={`tw-block tw-flex-1 tw-rounded-full ${isKept(t) ? "tw-bg-jam-purple" : "tw-bg-jam-ink/15"}`}
                style={{ height: `${10 + p * 72}%` }}
              />
            );
          })}
        </div>
        {sel && (
          <span
            className="tw-pointer-events-none tw-absolute tw-inset-y-1.5 tw-rounded-[16px] tw-border-[3px] tw-border-solid tw-border-jam-ink tw-bg-jam-lime/45"
            style={{ left: pct(from), width: pct(to - from) }}
          />
        )}
        {player.at !== null && (
          <span
            className="tw-pointer-events-none tw-absolute tw-inset-y-2 tw-w-[3px] tw-rounded-full tw-bg-jam-ink"
            style={{ left: pct(player.at) }}
          />
        )}
      </div>

      <div className="tw-relative tw--mt-1 tw-mx-3 tw-h-4">
        {Array.from(
          { length: Math.floor(secs / tickEvery) + 1 },
          (_, k) => k * tickEvery,
        ).map((t) => (
          <span
            key={t}
            className="tw-absolute tw-text-[11px] tw-font-extrabold tw-tabular-nums tw-text-jam-muted"
            style={{
              left: pct(t),
              transform: t === 0 ? undefined : "translateX(-50%)",
            }}
          >
            {clock(t)}
          </span>
        ))}
      </div>

      {sel && !sel.dragging ? (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-[24px] tw-bg-jam-lav tw-p-2 tw-pl-4">
          <span className="tw-mr-auto tw-flex tw-items-baseline tw-gap-2">
            <span className="jam-display tw-text-[18px] tw-font-extrabold tw-tabular-nums">
              {clock(from)}–{clock(to)}
            </span>
            <span className="tw-text-[13px] tw-font-extrabold tw-text-jam-muted">
              {sel.cut !== null
                ? t("train.clip.cut")
                : `${Math.round(to - from)}s`}
            </span>
          </span>
          <button
            type="button"
            onClick={() =>
              player.playing ? player.stop() : player.play(from, to)
            }
            className="tw-flex tw-h-11 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-white tw-px-5 tw-text-[15px] tw-font-extrabold tw-text-jam-ink hover:tw-bg-jam-line"
          >
            {player.playing ? (
              <Pause size={16} fill="currentColor" strokeWidth={0} />
            ) : (
              <Play size={16} fill="currentColor" strokeWidth={0} />
            )}
            {player.playing ? t("train.clip.stop") : t("train.clip.play")}
          </button>
          {sel.cut !== null ? (
            <button
              type="button"
              onClick={() => {
                onCuts(clip.cuts.filter((_, i) => i !== sel.cut));
                setSel(null);
              }}
              className="tw-flex tw-h-11 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-jam-ink tw-px-5 tw-text-[15px] tw-font-extrabold tw-text-white"
            >
              <Undo2 size={16} strokeWidth={2.75} />
              {t("train.clip.keepIt")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                onCuts(merge([...clip.cuts, [from, to]]));
                setSel(null);
                player.stop();
              }}
              className="tw-flex tw-h-11 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-jam-lime tw-px-5 tw-text-[15px] tw-font-extrabold tw-text-jam-ink hover:tw-bg-[#C4EE00]"
            >
              <Scissors size={16} strokeWidth={2.75} />
              {t("train.clip.cutIt")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setSel(null)}
            className="tw-h-11 tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-transparent tw-px-3 tw-text-[14px] tw-font-extrabold tw-text-jam-muted hover:tw-text-jam-ink"
          >
            {t("train.clip.cancel")}
          </button>
        </div>
      ) : (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <span className="tw-text-[13px] tw-font-bold tw-text-jam-muted">
            {t("train.clip.hint")}
          </span>
          {clip.cuts.length > 0 && (
            <button
              type="button"
              onClick={() => onCuts([])}
              className="tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-4 tw-text-[13px] tw-font-extrabold tw-text-jam-ink hover:tw-bg-jam-line"
            >
              <RotateCcw size={14} strokeWidth={2.75} />
              {t("train.clip.undo")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
